import { useState } from "react";
import { Link } from "react-router-dom";
import Footer from "@/components/site/Footer";
import BookingSidebar from "@/components/site/BookingSidebar";
import hero from "@/assets/tour-siem-reap-2day-angkor-kampong.jpg";
import kampongPhlukKids from "@/assets/tour-kampong-phluk-kids.jpg";

const itinerary = [
  {
    time: "Day 1 · Full Day",
    title: "Angkor Highlights  Sunrise, Angkor Thom & Ta Prohm",
    body:
      "Pre-dawn pickup (4:30–5:00 AM) for sunrise at Angkor Wat and a slow read of its bas-reliefs. Midday at Angkor Thom the South Gate and Bayon's smiling faces followed by lunch at a local restaurant. Afternoon in jungle-clad Ta Prohm and an optional sunset at Phnom Bakheng. Duration: 8–10 hours.",
  },
  {
    time: "Day 2 · Half Day",
    title: "Kampong Phluk Floating Village",
    body:
      "A flexible morning or afternoon drive (~30 km) to Kampong Phluk on Tonlé Sap. Boat through the stilt-house village, the flooded forest (best in wet season) and quiet fishing communities. Duration: 4–5 hours.",
  },
];

const sunriseSchedule = [
  {
    block: "Early Morning · 4:15 AM – 6:00 AM",
    items: [
      "Hotel pickup in a comfortable, air-conditioned vehicle",
      "Arrival at Angkor Archaeological Park",
      "Enter Angkor Wat via the quieter eastern entrance",
      "Walk through ancient corridors using a flashlight",
    ],
  },
  {
    block: "Sunrise Experience · 6:00 AM – 8:30 AM",
    items: [
      "Watch sunrise from the reflecting (library) pool",
      "Basic photography guidance from your guide",
      "Two-hour, in-depth exploration of the temple",
      "Discover the stories behind the bas-relief carvings",
    ],
  },
  {
    block: "Temple Exploration · 9:00 AM – 12:00 PM",
    items: [
      "Optional breakfast break (not included)",
      "Explore the jungle-covered temple of Ta Prohm",
      "Visit the ancient city of Angkor Thom",
      "Bayon Temple famous for its 200+ stone faces",
      "Walk the South Gate lined with gods and demons",
    ],
  },
  {
    block: "Return Journey · 12:30 PM – 1:30 PM",
    items: [
      "Comfortable drive back to your hotel",
      "Time to rest and reflect on your morning at Angkor",
    ],
  },
];

const optionOne = {
  label: "Small Group (2–15)",
  pricePrivate: "$120 – $160 per person",
  priceGroup: "From $95 per person",
  includes: [
    "Air-conditioned transportation with hotel pickup & drop-off",
    "Complimentary bottled water",
    "Refreshing cold towels",
    "Photography guidance for the best angles & lighting",
  ],
  excludes: [
    "Meals",
    "Temple entrance fee US$37 (valid for all temples in one day)",
    "Personal expenses & travel insurance",
  ],
};

const optionTwo = {
  label: "Private Experience",
  pricePrivate: "$280 – $420 total (1–2 pax)",
  priceGroup: "From $180 per person (3+)",
  includes: [
    "Private guide — full flexibility on pace & start time",
    "Air-conditioned transportation with hotel pickup & drop-off",
    "Complimentary bottled water & cold towels",
    "Photography guidance",
    "Multi-language options — German, Spanish, French, Italian",
  ],
  excludes: [
    "Meals",
    "Temple entrance fee — US$37",
    "Personal expenses & travel insurance",
  ],
};

const TourSiemReap2Day = () => {
  const [option, setOption] = useState(0);
  const opt = option === 0 ? optionOne : optionTwo;
  return (
    <main className="bg-background">
      {/* HERO */}
      <header className="relative bg-jungle text-background overflow-hidden">
        <img
          src={hero}
          alt="Private Angkor Wat sunrise tour with French-speaking guide reflecting in the lotus pool, Siem Reap Cambodia"
          width={1920}
          height={1280}
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-jungle/70 via-jungle/50 to-jungle/95" />
        <div className="relative z-10">
          <div className="container flex items-center justify-between py-6">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-display text-2xl tracking-wide">Aṅkor</span>
              <span className="text-[10px] uppercase tracking-[0.3em] opacity-70">Sojourns</span>
            </Link>
            <Link to="/tours" className="text-[11px] uppercase tracking-[0.3em] border-b border-background/40 pb-1 hover:border-primary hover:text-primary transition-colors">
              ← All Tours
            </Link>
          </div>
          <div className="container pt-20 pb-32 md:pt-28 md:pb-44 max-w-5xl">
            <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Two-Day Siem Reap Sojourn</p>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] text-balance">
              Angkor Temples <em className="font-light">and</em> Kampong Phluk two unhurried days.
            </h1>
            <p className="mt-10 max-w-2xl text-background/75 font-light text-lg leading-relaxed">
              A focused two-day pairing: sunrise at Angkor Wat with the great temples of the park on Day 1, and the stilt-house village and flooded forest of Kampong Phluk on Day 2.
            </p>
            <div className="mt-10 flex flex-wrap gap-x-10 gap-y-3 text-[11px] uppercase tracking-[0.3em] text-background/80">
              <span>Duration · 2 Days</span>
              <span>Group · 2–15</span>
              <span>From $95 / pax</span>
            </div>
          </div>
        </div>
      </header>

      {/* INTRO */}
      <section className="py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— The Idea</p>
          <h2 className="font-display text-4xl leading-[1.05] mb-10 text-balance md:text-xl">
            Two days, two distinct rhythms temples then the lake.
          </h2>
          <div className="space-y-6 text-lg font-light text-muted-foreground leading-relaxed">
            <p>
              Day one is the classic Angkor circuit at the right pace: sunrise at Angkor Wat, the smiling faces of Bayon and the jungle-bound corridors of Ta Prohm. Day two slows down with a half-day boat trip through Kampong Phluk's stilt houses, flooded forest and Tonlé Sap fishing communities.
            </p>
          </div>
        </div>
      </section>

      {/* ITINERARY */}
      <section className="pb-24 md:pb-32">
        <div className="container max-w-6xl"><div className="grid lg:grid-cols-[1fr_420px] gap-x-12 gap-y-12 items-start"><div>
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Itinerary</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-16 text-balance">
            Two days, day by day.
          </h2>
          <ol className="space-y-px">
            {itinerary.map((s, idx) => (
              <li
                key={idx}
                className="group grid grid-cols-[auto_1fr] md:grid-cols-[200px_1fr] gap-x-4 md:gap-x-12 min-w-0 border-t border-border py-8 md:py-10 transition-colors hover:bg-secondary/40 last:border-b"
              >
                <div className="flex flex-col items-start gap-y-2 text-base">
                  <span className="font-display text-primary tabular-nums text-xl">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <span className="uppercase tracking-[0.25em] text-primary text-xs md:text-base">
                    {s.time}
                  </span>
                </div>
                <div>
                  <h3 className="font-display text-2xl md:text-3xl leading-tight mb-3 transition-colors group-hover:text-primary">
                    {s.title}
                  </h3>
                  <p className="text-muted-foreground font-light leading-relaxed text-lg max-w-2xl">
                    {s.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div><BookingSidebar pricePerPerson={95} tourTitle="2-Day Siem Reap Tour: Angkor & Kampong Phluk" pricingTiers={[{ maxPax: 2, price: 95 }, { maxPax: 6, price: 125 }, { maxPax: 10, price: 155 }]} /></div></div>
      </section>

      {/* DAY 2 IMAGE */}
      <section className="pb-24 md:pb-32">
        <div className="container max-w-6xl">
          <div className="grid md:grid-cols-[1fr_1.2fr] gap-x-12 gap-y-8 items-center">
            <div className="overflow-hidden shadow-soft aspect-[3/4] bg-secondary">
              <img
                src={kampongPhlukKids}
                alt="Kampong Phluk floating village tour near Siem Reap — children playing by stilt houses, authentic Cambodia countryside experience"
                width={1080}
                height={1440}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Day 2 · Kampong Phluk</p>
              <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-8 text-balance">
                Life on the lake, unfiltered.
              </h2>
              <p className="text-lg font-light text-muted-foreground leading-relaxed">
                Stilt houses rising eight to ten metres above Tonlé Sap, painted boats lining the banks, and children at play in the slow brown current. Kampong Phluk is one of the last authentic floating villages far quieter than the tourist routes and a quiet reminder of how Cambodia lives with its great inland sea.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SUNRISE SCHEDULE */}
      <section className="bg-secondary py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Day 1 in Detail</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-16 text-balance">
            The complete sunrise schedule.
          </h2>
          <div className="space-y-12">
            {sunriseSchedule.map((s) => (
              <div key={s.block}>
                <h3 className="font-display text-2xl md:text-3xl mb-5">{s.block}</h3>
                <ul className="space-y-3 font-light text-lg text-muted-foreground">
                  {s.items.map((it) => (
                    <li key={it} className="border-b border-border pb-3 last:border-b-0">{it}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TOUR COLLECTION */}
      <section className="py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Why Us</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-10 text-balance">
            Not all Angkor tours are created equal.
          </h2>
          <div className="space-y-6 text-lg font-light text-muted-foreground leading-relaxed mb-12">
            <p>
              After planning thousands of trips to Cambodia, one thing is clear: some tours rush you through in a few hours, while others charge premium prices for a basic experience. Here's what truly makes the difference.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-10">
            <div>
              <h3 className="font-display text-2xl mb-3">Small Group Adventures (2–15)</h3>
              <p className="text-muted-foreground font-light">A more personal experience without the cost of a private tour.</p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-3">Private Temple Experiences</h3>
              <p className="text-muted-foreground font-light">Full flexibility explore at your own pace, start at sunrise, take breaks when you like.</p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-3">Multi-Language Options</h3>
              <p className="text-muted-foreground font-light">Tours in German, Spanish, French and Italian with native-speaking guides.</p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-3">Regional Departures</h3>
              <p className="text-muted-foreground font-light">Arriving from Singapore or Ho Chi Minh City? We coordinate your tour timing smoothly.</p>
            </div>
          </div>
          <div className="mt-12 border-l-2 border-primary pl-6 text-muted-foreground font-light">
            <p className="text-foreground uppercase tracking-[0.3em] text-xs mb-3">Good to know</p>
            <p>Sunrise tours are the most popular and tend to sell out quickly, especially during the dry season (November to March). Book at least 48 hours in advance earlier during peak season.</p>
          </div>
        </div>
      </section>

      {/* PRICING TABS */}
      <section className="bg-secondary py-24 md:py-32">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Pricing &amp; Inclusions</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
            Choose your level of comfort.
          </h2>

          <div role="tablist" aria-label="Pricing options" className="flex flex-wrap justify-center gap-10 md:gap-16 mb-16">
            {[optionOne, optionTwo].map((o, i) => {
              const isActive = i === option;
              return (
                <button
                  key={o.label}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setOption(i)}
                  className={`relative pb-3 uppercase tracking-[0.3em] transition-colors text-base ${
                    isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {o.label}
                  <span
                    className={`absolute left-0 right-0 -bottom-px h-px transition-colors ${
                      isActive ? "bg-primary" : "bg-transparent"
                    }`}
                  />
                </button>
              );
            })}
          </div>

          <ul className="mb-12 space-y-4 font-light text-lg max-w-3xl mx-auto">
            <li className="border-b border-border pb-4 flex justify-between gap-6"><span>Private (1–2 pax)</span><span className="text-right">{opt.pricePrivate}</span></li>
            <li className="pb-4 flex justify-between gap-6"><span>Group / Per Person</span><span className="text-right">{opt.priceGroup}</span></li>
          </ul>

          <div className="grid md:grid-cols-2 gap-x-12 gap-y-12">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Includes</p>
              <ul className="space-y-4 font-light text-lg">
                {opt.includes.map((x) => (
                  <li key={x} className="border-b border-border pb-4 last:border-b-0">{x}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Excludes</p>
              <ul className="space-y-4 font-light text-lg">
                {opt.excludes.map((x) => (
                  <li key={x} className="border-b border-border pb-4 last:border-b-0">{x}</li>
                ))}
              </ul>
            </div>
          </div>

          <p className="mt-10 text-sm text-muted-foreground font-light max-w-3xl mx-auto">
            Temple pass can be purchased online via the official Angkor Pass website.
          </p>
        </div>
      </section>

      {/* WHAT TO BRING */}
      <section className="py-24 md:py-32">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Essential Items</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-16 text-balance">What to bring, what to leave behind.</h2>
          <div className="grid md:grid-cols-3 gap-x-10 gap-y-12">
            <div>
              <h3 className="font-display text-2xl mb-5">Required</h3>
              <ul className="space-y-3 font-light text-muted-foreground">
                <li>Flashlight or phone light</li>
                <li>Temple pass (US$37)</li>
                <li>Modest clothing (shoulders &amp; knees covered)</li>
                <li>Comfortable walking shoes</li>
              </ul>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-5">Recommended</h3>
              <ul className="space-y-3 font-light text-muted-foreground">
                <li>Camera with extra batteries</li>
                <li>Small backpack</li>
                <li>Cash for breakfast</li>
                <li>Sun protection hat, sunscreen</li>
              </ul>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-5">Leave at Hotel</h3>
              <ul className="space-y-3 font-light text-muted-foreground">
                <li>Passport (bring a copy)</li>
                <li>Airline tickets</li>
                <li>Excess cash</li>
                <li>Valuable jewellery</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* GOOD TO KNOW */}
      <section className="bg-secondary py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Additional Information</p>
          <div className="space-y-6 text-lg font-light text-muted-foreground leading-relaxed">
            <p><span className="text-foreground">Pickup.</span> 4:15 AM – 4:35 AM (exact time confirmed one day before). Tour ends 12:30 PM – 1:30 PM.</p>
            <p><span className="text-foreground">Breakfast.</span> If your hotel includes breakfast, request a takeaway box, or stop at a local restaurant.</p>
            <p><span className="text-foreground">Dress code.</span> Casual but respectful covered shoulders and knees required inside temples. Comfortable walking shoes strongly recommended.</p>
            <p><span className="text-foreground">Weather.</span> Hot and dry throughout, with the chance of a passing shower. Midday is very hot but most woodland sites have low humidity.</p>
            <p><span className="text-foreground">Food.</span> Khmer cuisine is similar to most Southeast Asian cooking, but rather less spicy. Good bread is widely available thanks to the French influence, alongside a surprising range of local brews.</p>
            <p>For safety, leave valuables at your hotel and bring only what you need. This tour is not wheelchair accessible. Children under 8 are not permitted. Maximum group size: 2–15 travellers in an air-conditioned vehicle.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="container max-w-3xl text-center">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Reserve Your Journey</p>
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05] mb-10 text-balance">
            Two days. Angkor at sunrise, Tonlé Sap by boat.
          </h2>
          <Link
            to="/#contact"
            className="inline-block bg-foreground text-background px-10 py-4 text-[11px] uppercase tracking-[0.3em] hover:bg-primary transition-colors"
          >
            Book Now →
          </Link>
        </div>
      </section>
      <Footer />
    </main>
  );
};

export default TourSiemReap2Day;
